import React from 'react';
import { Box, Button, TextField, Tooltip } from '@mui/material';
import MCheckbox from '../../../components/controls/MCheckbox';
import MRadio from '../../../components/controls/MRadio';
import MSelect from '../../../components/controls/MSelect';
import { STATUS_CHOICES } from './utils';

/** Props for {@link FilterBar}. */
interface FilterBarProps {
    onExistsFilterChange: (value: boolean | null) => void;
    cityFilter: string;
    onCityFilterChange: (value: string) => void;
    stateFilter: string;
    onStateFilterChange: (value: string) => void;
    invalidOnlyFilter: boolean;
    onInvalidOnlyFilterChange: (value: boolean) => void;
    /** `false` (default) = Normal filtering behavior; `true` = Preserve Selection mode (see {@link CityStateDetailPopup}). */
    editingMode: boolean;
    onEditingModeChange: (value: boolean) => void;
    applyStatusValue: string;
    onApplyStatusValueChange: (value: string) => void;
    onApplyStatus: () => void;
}

/**
 * Filter/action bar shown above the City/State grid: the **Exists In DB** radio (All/Yes/No,
 * default No), City/State text filters, an **Invalid Only** checkbox, a **Preserve Selection**
 * checkbox (tooltipped — see {@link CityStateDetailPopup} for the selection-persistence
 * semantics it drives internally via `editingMode`), and the bulk **Apply Status** control.
 */
const FilterBar: React.FC<FilterBarProps> = ({
    onExistsFilterChange, cityFilter, onCityFilterChange, stateFilter, onStateFilterChange,
    invalidOnlyFilter, onInvalidOnlyFilterChange, editingMode, onEditingModeChange,
    applyStatusValue, onApplyStatusValueChange, onApplyStatus,
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 220 }}>
            <MRadio
                label="Exists In DB"
                choices={[{ id: 'All', name: 'All' }, { id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]}
                defaultValue="No"
                onChange={(id: string) => onExistsFilterChange(id === 'All' ? null : id === 'Yes')}
            />
        </Box>
        <TextField
            label="City"
            size="small"
            value={cityFilter}
            onChange={(e) => onCityFilterChange(e.target.value)}
            sx={{ width: 200 }}
        />
        <TextField
            label="State"
            size="small"
            value={stateFilter}
            onChange={(e) => onStateFilterChange(e.target.value)}
            sx={{ width: 160 }}
        />
        <MCheckbox
            label="Invalid Only"
            defaultValue={invalidOnlyFilter}
            onChange={(checked: boolean) => onInvalidOnlyFilterChange(checked)}
        />
        <Tooltip title="Maintains current row selections and edits while filtering or refreshing the grid." placement="top">
            <Box sx={{ display: 'inline-flex' }}>
                <MCheckbox
                    label="Preserve Selection"
                    defaultValue={editingMode}
                    onChange={(checked: boolean) => onEditingModeChange(checked)}
                />
            </Box>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Box sx={{ width: 110 }}>
                <MSelect
                    label="Status"
                    value={applyStatusValue}
                    isEmptyOption={false}
                    choices={STATUS_CHOICES}
                    onChange={(value: string) => onApplyStatusValueChange(value)}
                />
            </Box>
            <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={onApplyStatus}
                sx={{ whiteSpace: 'nowrap' }}
            >
                Apply Status
            </Button>
        </Box>
    </Box>
);

export default FilterBar;
